import { Button, Modal, Spin } from 'antd'
import classNames from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { MyIcon } from '~/components/ui/MyIcon'
import { controlnetPatternArr, pattern2Header } from '~/configs/ai'
import { TRANSLATE_PROMPT } from '~/graphqls/gqls'
import {
  AiArtworkPromptEnum,
  AiArtworkServiceTypeGraphQLEnum,
  AiArtworkShareSource,
  AiArtworkSource,
  AiCreateArtworkModel,
  FollowStatus,
  PostTypes,
  Post_AiArtworkInfo,
  Post_Artwork,
  SquarePostScope,
  VisibleType,
  modelOptions,
  translatePrompt,
  translatePromptVariables,
  ControlShowType,
  ModelVersionEnum,
  SuperType,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { usePostSocial } from '~/hooks/usePostSocial'
import { isSpecialSymbol, isWordBetweenSymbol } from '~/utils'
import FollowBtn from '../../business/FollowBtn'
import CopyParams from '../ai/CopyParams'
import { useShowModel } from '../ai/useShowModel'
import styles from './AiItemInfo.module.css'
import { SameAgainBtn } from './ReplyBar'
import { Gallery } from 'wujieai-react-icon'
import { CopyText } from '~/components/business/CopyText'

interface Props {
  data: Post_AiArtworkInfo
  post: PostTypes
  viewPermission?: VisibleType | null
  hasCopy?: boolean
  hasAiParamCopy?: boolean
  isSelf?: boolean
  competitionData?: Post_Artwork
  [key: string]: any
}
const followStatusDesc = {
  [FollowStatus.NON]: { text: '关注', type: 'primary' },
  [FollowStatus.HE_ONE_WAY]: { text: '关注', type: 'primary' },
  [FollowStatus.ME_ONE_WAY]: { text: '已关注', type: 'primary' },
  [FollowStatus.TWO_WAY]: { text: '已关注', type: 'primary' },
}
const HOST = process.env.NEXT_PUBLIC_HOST
export interface IAiItemInfoRef {
  setModalOpen: (param: string) => void
}
/**
 *
 * @param data ai作品信息
 * @param viewPermission 0：全部可见；1：粉丝可见；2：互关可见
 * @param hasCopy 有复制描述按钮
 */
const AiItemInfo: React.ForwardRefRenderFunction<IAiItemInfoRef, Props> = (
  {
    data: product,
    competitionData,
    viewPermission = VisibleType.ALL,
    hasCopy = false,
    hasAiParamCopy = false,
    hasTranslate = false,
    post,
    followStatus,
    onFollowChange,
    isSelf,
  },
  ref
) => {
  const router = useRouter()
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState<string[]>()

  const [isTranslated, setIsTranslated] = useState(false)
  const [translateLoading, setTranslateLoading] = useState(false)
  const [translatePrompt, setTranslatePrompt] = useState(
    product?.chinesePrompt || ''
  )

  const [isUcPromptTranslated, setIsUcPromptTranslated] = useState(false)
  const [translateUcPrompt, setTranslateUcPrompt] = useState(
    product.chinesePromptDd || ''
  )
  const [translateUcPromptLoading, setTranslateUcPromptLoading] =
    useState(false)

  const [modalOpen, setModalOpen] = useState('')
  const { handleSameAgain } = usePostSocial(post, {
    sameAgainSuccess: () => {
      // 关闭弹窗
      setModalOpen('')
    },
  })

  /** 风格模型 */
  const isST = product.model === AiCreateArtworkModel.STYLE
  const isMJ = product.model === AiCreateArtworkModel.MIDJOURNEY
  const isMJ6 = [303, 304, 305].includes(product?.modelCode!)
  const is4k = product.superType === SuperType.FOUR_K
  const isFlux = product.modelInfo?.modelVersion === 'flux'

  const { getSubModelName } = useShowModel()

  const onVote = () => {
    window.open(`${HOST}/ai-competition/product/${competitionData?.id}`)
  }
  // 是否显示信息 可见类型
  const [showInfo, visibleType, visibleMap] = useMemo(() => {
    let visibleType = ''
    const visibleMap = {
      text: '',
      action: (
        <Button
          type="primary"
          onClick={() => {
            setModalOpen('')
          }}
        >
          确定
        </Button>
      ),
    }
    if (viewPermission === VisibleType.ONLY_FANS) {
      visibleType = '关注可见'
      visibleMap.text = '关注'
      visibleMap.action = (
        <FollowBtn
          dark
          targetId={post?.user?.uid as number}
          status={followStatus}
          onChange={status => {
            setModalOpen('')
            return onFollowChange(status)
          }}
          statusDesc={followStatusDesc}
        />
      )
    } else if (viewPermission === VisibleType.ONLY_FOLLOW_EACH_OTHER) {
      visibleType = '互相关注可见'
      visibleMap.text = '互相关注'
      if (followStatus !== FollowStatus.ME_ONE_WAY) {
        visibleMap.action = (
          <FollowBtn
            dark
            targetId={post?.user?.uid as number}
            status={followStatus}
            onChange={status => {
              setModalOpen('')
              return onFollowChange(status)
            }}
            statusDesc={followStatusDesc}
          />
        )
      }
    } else if (viewPermission === VisibleType.ONLY_VOTED) {
      visibleType = '投票可见'
      visibleMap.text = '投票'
      visibleMap.action = (
        <Button type="primary" onClick={onVote}>
          去投票
        </Button>
      )
    } else if (viewPermission === VisibleType.CREATE_LIKEWISE) {
      visibleType = '仅一键同款可见'
      visibleMap.text = '一键同款'
      visibleMap.action = (
        <SameAgainBtn
          post={post}
          handleSameAgain={handleSameAgain}
          hideCount
          hidePictureSame
        />
      )
    } else if (viewPermission === VisibleType.CREATE_LIKEWISE_COST) {
      visibleType = '仅一键同款可见'
      visibleMap.text = '一键同款'
      visibleMap.action = (
        <SameAgainBtn
          post={post}
          handleSameAgain={handleSameAgain}
          hideCount
          hidePictureSame
        />
      )
    } else if (viewPermission === VisibleType.NONE) {
      visibleType = '未公开'
      visibleMap.text = '未公开'
    }

    return [!product?.hiddenBaseInfo, visibleType, visibleMap]
  }, [product])

  const genInfos = (value?: string | number) => {
    if (showInfo) return value
    return visibleType
  }

  const genImageUrl = (product: Post_AiArtworkInfo) => {
    const imgUrls =
      product.multiReferenceImageUrlList
        ?.filter(item => !!item)
        .map(item => item!) ?? []
    if (imgUrls.length == 0) return undefined

    return genImageUrl_1(imgUrls)
  }

  const genImageUrl_1 = (imgUrls: string[]) => {
    return (
      <span
        onClick={() => {
          if (!showInfo) {
            return setModalOpen('才可查看创作信息')
          }
          setPreviewImage(imgUrls)
          setPreviewVisible(true)
        }}
        className="text-primary cursor-pointer flex items-center justify-end"
      >
        <span>查看</span>{' '}
        <MyIcon icon="arrow-right-simple" className="text-[24px]" />
      </span>
    )
  }

  const genPrompt = (value?: string) => {
    //;兼容下末尾兼容下末尾不是符号导致不能加上span的问题
    let str = value?.toString() || ''
    let lstStr = str.slice(-1)
    if (/[^,;。，]/.test(lstStr)) {
      str = `${str};`
      return str?.replace(isWordBetweenSymbol, '<span>$&</span>').slice(0, -1)
    }
    return str?.replace(isWordBetweenSymbol, '<span>$&</span>')
  }

  /** 判断描述词中是否存在完整一段英文，展示翻译按钮 */
  const genHasTranslate = (value?: string) => {
    if (!value) return false

    value = value.replace(/[\(\)\{\}\[\]\-]/g, '')
    return value.split(',').some(it => /^[a-zA-Z\s]+$/g.test(it))
  }
  /** 风格参考、角色参考、结构参考 */
  const genInstantStyle = (product: Post_AiArtworkInfo) => {
    if (product.modelInfo?.modelVersion !== ModelVersionEnum.SDXL) {
      return []
    }
    const instanceArray = [
      {
        showType: ControlShowType.STYLE,
        name: '风格参考',
      },
      {
        showType: ControlShowType.FACEID,
        name: '角色参考',
      },
      {
        showType: ControlShowType.COMPOSITION,
        name: '结构参考',
      },
    ]
    return instanceArray.map(item => {
      const referTo = product.instantStyle?.find(
        it => it.showType === item.showType
      )
      return {
        isShow: !isST && !is4k,
        name: item.name,
        value: referTo ? genImageUrl_1([referTo.imageUrl]) : '无',
      }
    })
  }
  const infoItems = [
    {
      name: '模型主题',
      value: genInfos(
        [
          ...modelOptions,
          {
            name: 'MJ模型',
            model: AiCreateArtworkModel.PRETEND_MJ,
          },
        ]?.filter(it => it.model === product?.model)?.[0]?.name +
          getSubModelName(product)
      ),
      isShow: true,
    },
    {
      name: '标签选择',
      value:
        genInfos(
          product?.styleDecorationOptions?.map(item => item.name)?.join(',')
        ) || '无',
      isShow: !isST && !isMJ,
    },
    {
      name: '采样模式',
      value:
        genInfos(product?.samplerModelInfo?.samplerModelName as string) || '无',
      isShow:
        !isST && !isMJ && product.superType !== SuperType.FOUR_K && !isFlux,
    },
    {
      name: '画面尺寸',
      value:
        genInfos(
          product?.serviceType === AiArtworkServiceTypeGraphQLEnum.PROFESSIONAL
            ? `${product.width}x${product.height}`
            : (`${product.size}` as string)
        ) || '无',
      isShow: true,
    },
    {
      name: '标签选择',
      value:
        genInfos(
          product?.styleDecorationOptions?.map(item => item.name)?.join(',')
        ) || '无',
      isShow: isMJ,
    },
    {
      name: '随机种子',
      value: genInfos(product?.seed as string) || '无',
      isShow: !isST,
    },
    {
      name: '提示词相关性',
      value: genInfos(product?.cfg as number) ?? '无',
      isShow: !isST && !isMJ && !is4k,
    },
    {
      name: 'Clip skip',
      value: genInfos(product?.clipSkip as number) ?? '无',
      isShow: !isST && !isMJ && !isFlux,
    },
    {
      name: 'ENSD',
      value: genInfos(product?.ensd as number) ?? '无',
      isShow: !isST && !isMJ && !isFlux,
    },
    {
      name: '角色同人',
      value:
        genInfos(
          product?.characterOptions?.map(item => item.name)?.join(',')
        ) || '无',
      isShow: !isST && !isMJ && !isFlux,
    },
    {
      name: '融合模型',
      value:
        genInfos(
          (product?.modelFusionOptions || ([] as any[]))
            .concat(
              product?.userTrainedModelOption?.map(it => ({
                name: it.trainedModel?.modelName,
                weight: it.weight,
              })) || []
            )
            ?.map(item => `${item.name}(${item.weight})`)
            ?.join(' , ')
        ) || '无',
      isShow: !isST && !isMJ,
    },
    {
      name: '风格差异化',
      value: genInfos(product?.mjParam?.chaos ?? '无'),
      isShow: isMJ,
    },
    {
      name: '风格艺术化',
      value: genInfos(product?.mjParam?.stylize ?? '无'),
      isShow: isMJ,
    },
    {
      name: 'QUALITY',
      value: genInfos(
        !!product?.mjParam?.quality ? Number(product.mjParam.quality) : '无'
      ),
      isShow: isMJ,
    },
    {
      name: 'UPBETA',
      value: genInfos(
        typeof product?.mjParam?.upbeta === 'boolean'
          ? product.mjParam.upbeta
            ? '是'
            : '否'
          : '无'
      ),
      isShow: isMJ,
    },
    {
      name: 'TILE',
      value: genInfos(
        typeof product?.mjParam?.tile === 'boolean'
          ? product.mjParam.tile
            ? '是'
            : '否'
          : '无'
      ),
      isShow: isMJ,
    },
    {
      name: '风格参考',
      value: product?.mjParam?.srefUrls?.length
        ? genImageUrl_1(product?.mjParam?.srefUrls as any)
        : '无',
      isShow: isMJ && isMJ6,
    },
    {
      name: '风格参考强度',
      value: product?.mjParam?.sw ? Number(product?.mjParam?.sw) : '无',
      isShow: isMJ && isMJ6 && product?.mjParam?.srefUrls?.length,
    },
    {
      name: '角色参考',
      value: product?.mjParam?.crefUrls?.length
        ? genImageUrl_1(product?.mjParam?.crefUrls as any)
        : '无',
      isShow: isMJ && isMJ6,
    },
    {
      name: '角色参考强度',
      value: product?.mjParam?.cw ? Number(product?.mjParam?.cw) : '无',
      isShow: isMJ && isMJ6 && product?.mjParam?.crefUrls?.length,
    },
    {
      name: '参考图',
      value: genImageUrl(product) || '无',
      isShow: !isST && !is4k,
    },
    {
      name: '步长比例',
      value: genInfos(product?.cutDiffusion?.cutdiffProportion ?? '无'),
      isShow: is4k,
    },
    {
      name: 'CFG Scale-1',
      value: genInfos(product?.cutDiffusion?.cfgScale1 ?? '无'),
      isShow: is4k,
    },
    {
      name: 'CFG Scale-2',
      value: genInfos(product?.cutDiffusion?.cfgScale2 ?? '无'),
      isShow: is4k,
    },
    {
      name: '风格参数',
      value: genInfos(product?.flux?.style ?? '无'),
      isShow: isFlux && !product.initImageUrl,
    },
    {
      name: '混沌参数',
      value: genInfos(product?.flux?.chaos ?? '无'),
      isShow: isFlux && !product.initImageUrl,
    },
    ...genInstantStyle(product),
  ]

  const prefineItems = [
    {
      name: '精绘倍数',
      value: genInfos(product?.prefineInfo?.multiple as number) || '无',
    },
    {
      name: '强度',
      value: genInfos(`${product?.prefineInfo?.strength}%` as string) || '无',
    },
  ]

  const controlnetItems = [
    {
      name: '使用类型',
      value:
        genInfos(
          pattern2Header[product.pattern! as keyof typeof pattern2Header]?.title
        ) || '无',
    },
    {
      name: '创意度',
      value:
        genInfos(`${(product.creativityDegree ?? 50).toFixed(0)}%` as string) ||
        '无',
    },
    // {
    //   name: '预处理图片',
    //   value: genImageUrl(product?.pretreatmentImage?.url as string) || '无',
    //   action: () => {
    //     if (!product?.pretreatmentImage?.url) return
    //     if (!showInfo) {
    //       return setModalOpen('才可查看创作信息')
    //     }
    //     setPreviewImage(product?.pretreatmentImage?.url)
    //     setPreviewVisible(true)
    //   },
    // },
  ]

  const translateAction = useImperativeQuery<
    translatePrompt,
    translatePromptVariables
  >(TRANSLATE_PROMPT)

  const onTranslatePrompt = () => {
    if (isTranslated) {
      setIsTranslated(false)
      return
    }
    if (translatePrompt.length) {
      setIsTranslated(true)
      return
    }
    setTranslateLoading(true)
    translateAction({
      aiArtworkId: product.id,
      aiArtworkSource:
        post.postScope === SquarePostScope.INSIDE
          ? AiArtworkSource.INSIDE
          : AiArtworkSource.EXTERNAL,
      promptEnum: AiArtworkPromptEnum.INPUT_PROMPT,
    })
      .then(res => {
        setTranslatePrompt(res?.data?.aiArtworkResourceModule?.result || '')
      })
      .finally(() => {
        setTranslateLoading(false)
        setIsTranslated(true)
      })
  }

  const onTranslateUcPrompt = () => {
    if (isUcPromptTranslated) {
      setIsUcPromptTranslated(false)
      return
    }
    if (translateUcPrompt.length) {
      setIsUcPromptTranslated(true)
      return
    }
    setTranslateUcPromptLoading(true)
    translateAction({
      aiArtworkId: product.id,
      aiArtworkSource:
        post.postScope === SquarePostScope.INSIDE
          ? AiArtworkSource.INSIDE
          : AiArtworkSource.EXTERNAL,
      promptEnum: AiArtworkPromptEnum.INPUT_PROMPT_DD,
    })
      .then(res => {
        setTranslateUcPrompt(res?.data?.aiArtworkResourceModule?.result || '')
        setIsUcPromptTranslated(true)
      })
      .finally(() => {
        setTranslateUcPromptLoading(false)
      })
  }

  const toSearch = (e: any) => {
    e.preventDefault()
    // @ts-ignore
    const localName = e.target.localName
    if (localName !== 'span') return
    // @ts-ignore
    let keywords = e.target.innerText
    keywords = keywords.trim().replace(isSpecialSymbol, '')
    if (!keywords?.length) return
    router.push({
      pathname: '/',
      query: {
        keywords,
      },
    })
  }

  useImperativeHandle(ref, () => ({
    setModalOpen,
  }))

  useEffect(() => {
    setIsTranslated(false)
    setTranslatePrompt(product?.chinesePrompt || '')
    setIsUcPromptTranslated(false)
    setTranslateUcPrompt(product?.chinesePromptDd || '')
  }, [product])

  return (
    <>
      <div className={classNames('bg-neutral-200 rounded-2xl px-4 py-3 mt-4')}>
        {showInfo && (
          <div className="text-sm font-medium text-neutral-500 pb-2.5 mb-2.5 border-b border-neutral-300 flex justify-between">
            <span>画面描述</span>
            <div className="flex">
              {hasTranslate && genHasTranslate(product?.prompt as string) && (
                <div
                  className={classNames(
                    'text-neutral-400 transition-colors hover:text-primary cursor-pointer',
                    {
                      '!text-primary': isTranslated,
                    }
                  )}
                  onClick={onTranslatePrompt}
                >
                  <MyIcon icon="translate" /> 翻译
                </div>
              )}
              {/* {hasCopy && (
                <div className="text-neutral-400 transition-colors hover:text-primary ml-4">
                  <CopyText text={product?.prompt as string}>
                    <MyIcon icon="copy" /> 复制
                  </CopyText>
                </div>
              )} */}

              {/* 同款过的数据，下方按钮变成全参后，描述词这里改成复制 */}
              {post?.sameCreated && showInfo && (
                <div className="text-primary transition-colors ml-4">
                  <CopyText text={product?.prompt as string}>
                    <MyIcon icon="copy" /> 复制
                  </CopyText>
                </div>
              )}

              {!post?.sameCreated && showInfo && hasAiParamCopy && (
                <CopyParams
                  className="ml-4"
                  title={post.title as string}
                  product={product}
                  source={AiArtworkShareSource.SQUARE_POST}
                >
                  <div className="flex items-center font-medium text-primary cursor-pointer">
                    <MyIcon icon="copy" className="mr-1" />
                    <span>全参复制</span>
                  </div>
                </CopyParams>
              )}
            </div>
          </div>
        )}
        <div className="text-sm text-neutral-800 break-all">
          {showInfo ? (
            <>
              <div
                className={styles.prompt}
                dangerouslySetInnerHTML={{
                  __html: genPrompt(product?.prompt as string),
                }}
                onClick={toSearch}
              ></div>
              {translateLoading && (
                <div className="mt-2.5 text-center">
                  <Spin size="default" />
                </div>
              )}
              {isTranslated && (
                <div
                  className={classNames('mt-2.5', styles.prompt)}
                  dangerouslySetInnerHTML={{
                    __html: genPrompt(translatePrompt),
                  }}
                  onClick={toSearch}
                ></div>
              )}
            </>
          ) : (
            <div className="text-center text-neutral-500 text-sm">
              <div className="mb-1">
                <MyIcon icon="eyeslash" className="text-[24px]" />
              </div>
              {viewPermission === VisibleType.CREATE_LIKEWISE_COST
                ? `本作品一键同款需支付${post.createLikewiseCast || 1}积分`
                : `${visibleType}画面描述`}
              {viewPermission === VisibleType.ONLY_VOTED && (
                <span className="text-primary cursor-pointer" onClick={onVote}>
                  &nbsp;{'前往投票 >'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {/* 不可见情况下，状态样式和画面描述一致，这个缺省 */}
      {showInfo && !!product.ucPrompt && (
        <div
          className={classNames('bg-neutral-200 rounded-2xl px-4 py-3 mt-4')}
        >
          <div className="text-sm font-medium text-neutral-500 pb-2.5 mb-2.5 border-b border-neutral-300 flex justify-between">
            <span>负面描述</span>
            <div className="flex">
              {hasTranslate && genHasTranslate(product?.ucPrompt as string) && (
                <div
                  className={classNames(
                    'text-neutral-400 transition-colors hover:text-primary cursor-pointer',
                    {
                      '!text-primary': isUcPromptTranslated,
                    }
                  )}
                  onClick={onTranslateUcPrompt}
                >
                  <MyIcon icon="translate" /> 翻译
                </div>
              )}
              {/* {hasCopy && (
                <div className="text-neutral-400 transition-colors hover:text-primary ml-4">
                  <CopyText text={product?.ucPrompt as string}>
                    <MyIcon icon="copy" /> 复制
                  </CopyText>
                </div>
              )} */}
            </div>
          </div>
          <div className="text-sm text-neutral-800 break-all">
            <div
              className={styles.prompt}
              dangerouslySetInnerHTML={{
                __html: genPrompt(product?.ucPrompt as string),
              }}
              onClick={toSearch}
            ></div>
            {translateUcPromptLoading && (
              <div className="mt-2.5 text-center">
                <Spin size="default" />
              </div>
            )}
            {isUcPromptTranslated && (
              <div
                className={classNames('mt-2.5', styles.prompt)}
                dangerouslySetInnerHTML={{
                  __html: genPrompt(translateUcPrompt),
                }}
                onClick={toSearch}
              ></div>
            )}
          </div>
        </div>
      )}
      <div className={classNames('bg-neutral-200 rounded-2xl px-4 py-3 mt-4')}>
        <div className="flex justify-between pb-2.5 mb-2.5 border-b border-neutral-300 ">
          <div className="text-sm font-medium text-neutral-500 flex justify-start items-center relative">
            <MyIcon icon="cube" className="mr-2 text-[20px]" />
            <span>创作信息</span>
          </div>
          {!!product.extend?.originalSquarePost?.id && (
            <div className="flex items-center text-primary font-medium cursor-pointer">
              <Gallery size={16} />
              <span className="ml-1">
                <Link href={`/plaza/${product.extend?.originalSquarePost?.id}`}>
                  查看同款来源
                </Link>
              </span>
            </div>
          )}
        </div>

        <div className={styles.infos}>
          {infoItems
            .filter(item => item?.isShow)
            .map(it => (
              <div key={it?.name}>
                <span>{it?.name}</span>
                <span>{it?.value}</span>
              </div>
            ))}
        </div>
      </div>
      {!!product?.prefineInfo && !is4k && (
        <div
          className={classNames('bg-neutral-200 rounded-2xl px-4 py-3 mt-4')}
        >
          <div className="text-sm font-medium text-neutral-500 pb-2.5 mb-2.5 border-b border-neutral-300 flex justify-start items-center">
            <MyIcon icon="image" className="mr-2 text-[20px]" />
            <span>精绘</span>
          </div>
          <div className={styles.infos}>
            {prefineItems.map(it => (
              <div key={it.name}>
                <span>{it.name}</span>
                <span>{it.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {controlnetPatternArr.includes(product.pattern!) && (
        <div
          className={classNames('bg-neutral-200 rounded-2xl px-4 py-3 mt-4')}
        >
          <div className="text-sm font-medium text-neutral-500 pb-2.5 mb-2.5 border-b border-neutral-300 flex justify-start items-center">
            <MyIcon icon="controlNet" className="mr-2 text-[20px]" />
            <span>ControlNet</span>
          </div>
          <div className={styles.infos}>
            {controlnetItems.map(it => (
              <div key={it.name}>
                <span>{it.name}</span>
                <span>{it.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Lightbox
        open={previewVisible}
        close={() => setPreviewVisible(false)}
        index={0}
        slides={(previewImage ?? []).map(item => {
          return { src: item! }
        })}
        render={{
          iconPrev: () => (
            <div className="flex rounded-full w-10 h-10 border-white border-2 justify-center items-center">
              <MyIcon icon="arrow-left" className="text-lg" />
            </div>
          ),
          iconNext: () => (
            <div className="flex rounded-full w-10 h-10 border-white border-2 justify-center items-center">
              <MyIcon icon="arrow-right" className="text-lg" />
            </div>
          ),
        }}
        carousel={{
          finite: true,
          preload: 2,
          imageFit: 'contain',
        }}
        animation={{ fade: 250, swipe: 500 }}
      />
      <Modal
        title="提示"
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.6)',
        }}
        width={270}
        className={classNames('hookModal dark', styles.modalWrap)}
        open={!!modalOpen}
        footer={
          <div
            className={classNames({
              [styles.likeWiseCost]:
                viewPermission === VisibleType.CREATE_LIKEWISE_COST,
            })}
          >
            <Button
              onClick={() => {
                setModalOpen('')
              }}
            >
              取消
            </Button>
            {visibleMap.action}
          </div>
        }
        destroyOnClose
      >
        <div className="text-neutral-400 text-sm text-center">
          您需{visibleMap.text}
          {modalOpen}
        </div>
      </Modal>
    </>
  )
}
export default forwardRef(AiItemInfo)
